import Vue from 'vue'
import VueApollo from 'vue-apollo'
import { Storage } from '@capacitor/storage'
import { setContext } from 'apollo-link-context'
import { createApolloClient, restartWebsockets } from 'vue-cli-plugin-apollo/graphql-client'

import { dispatchError } from './mixins/handleGraphqlError'
import { clearContractId } from '@/mixins/currContractId'
import { clearContractsStatus } from '@/mixins/hasActiveContracts'

import { customFetch } from './customFetch'

// Install the vue plugin
Vue.use(VueApollo)

// Name of the localStorage item
export const AUTH_TOKEN = 'apollo-token'
export const REFRESH_TOKEN = 'rtk'
export const SOURCE = 'source'

const getLocale = async () => {
  const fiveDigitsLang = {
    ru: 'ru-RU',
    en: 'en-US',
    es: 'es-MX',
    uk: 'uk-UA',
    kk: 'kk-KZ'
  }

  let lang = await Storage.get({ key: 'lang' }).then(res => res.value)

  if (!lang) {
    const localLang = navigator.language || navigator.userLanguage
    if (localLang.match(/-/gi)) {
      lang = localLang.split(/-/gi)[0]
    } else {
      lang = localLang
    }
  }

  return fiveDigitsLang[lang]
}

const getSource = async () => {
  return await Storage.get({ key: SOURCE }).then(res => res.value)
}

const localeLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      'x-language': await getLocale(),
      'x-source': await getSource()
    }
  }
})

// Config
const defaultOptions = {
  // You can use `https` for secure connection (recommended in production)
  // httpEndpoint,
  // You can use `wss` for secure connection (recommended in production)
  // Use `null` to disable subscriptions
  // wsEndpoint: process.env.VUE_APP_GRAPHQL_WS || 'ws://localhost:4000/graphql',
  wsEndpoint: null,
  // LocalStorage token
  tokenName: AUTH_TOKEN,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,

  // Override default apollo link
  // note: don't override httpLink here, specify httpLink options in the
  // httpLinkOptions property of defaultOptions.
  // link: myLink
  // defaultHttpLink: false,
  httpLinkOptions: {
    // uri: httpEndpoint,
    credentials: 'include',
    fetch: customFetch
  },

  // Override default cache
  // cache: myCache

  // Override the way the Authorization header is set
  getAuth: async (tokenName) => {
    return await Storage.get({ key: tokenName }).then(res => res.value)
  },

  link: localeLink

  // Additional ApolloClient options
  // apollo: { ... }

  // Client local data (see apollo-link-state)
  // clientState: { resolvers: { ... }, defaults: { ... } }
}

// Call this in the Vue app file
export function createProvider (options = {}, domainList) {
  const generalOptions = Object.assign({}, defaultOptions)
  const endpoint = domainList.getActiveEndpoint
  generalOptions.httpEndpoint = endpoint
  generalOptions.httpLinkOptions.uri = endpoint

  // Create apollo client
  const { apolloClient, wsClient } = createApolloClient({
    ...generalOptions,
    ...options
  })
  apolloClient.wsClient = wsClient

  // Create vue apollo provider
  return new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        // fetchPolicy: 'cache-and-network',
      }
    },
    errorHandler (error) {
      dispatchError(error)
      // eslint-disable-next-line no-console
      // console.log('%cError', 'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;', error.message)
    }
  })
}

export const hasApolloToken = async function () { return typeof Storage !== 'undefined' && await Storage.get({ key: AUTH_TOKEN }).then(res => res.value) }
export const hasRefreshToken = async function () { return typeof Storage !== 'undefined' && await Storage.get({ key: REFRESH_TOKEN }).then(res => res.value) }

// Manually call this when need to refresh the token
export async function refreshTokenSet (apolloClient, token, refreshToken) {
  if (typeof Storage !== 'undefined' && token) {
    await Storage.set({
      key: AUTH_TOKEN,
      value: token
    })
  }

  if (typeof Storage !== 'undefined' && refreshToken) {
    await Storage.set({
      key: REFRESH_TOKEN,
      value: refreshToken
    })
  }
}

// Manually call this when user log in
export async function onLogin (apolloClient, token, refreshToken, isAdmin) {
  if (typeof Storage !== 'undefined' && token) {
    await Storage.set({
      key: AUTH_TOKEN,
      value: token
    })
  }

  if (typeof Storage !== 'undefined' && refreshToken) {
    await Storage.set({
      key: REFRESH_TOKEN,
      value: refreshToken
    })
  }

  if (isAdmin) {
    await Storage.set({
      key: SOURCE,
      value: 'admin'
    })
  }

  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient)
  try {
    await apolloClient.resetStore()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('%cError on cache reset (login)', 'color: orange;', e.message)
  }
}

// Manually call this when user log out
export async function onLogout (apolloClient) {
  if (typeof Storage !== 'undefined') {
    await Storage.remove({
      key: AUTH_TOKEN
    })
  }

  if (typeof Storage !== 'undefined') {
    await Storage.remove({
      key: REFRESH_TOKEN
    })
  }

  if (typeof Storage !== 'undefined') {
    await Storage.remove({
      key: SOURCE
    })
  }
  // if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient)
  try {
    await Promise.all([
      await apolloClient.resetStore(),
      await clearContractId()
    ])

    clearContractsStatus()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('%cError on cache reset (logout)', 'color: orange;', e.message)
  }
}
